import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Car,
  CarFormOption,
  Company,
  CompanyFetchOption,
  Driver,
  DriverFormOption,
  penaltyCreateSchema,
  PenaltyCreateFormValues,
} from '~/shared/api'
import {
  AsyncAutocompleteInput,
  DatePickerInput,
  FormButtons,
  FormProvider,
  MaskInput,
  NumericInput,
  TextareaInput,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { OdpsRecipient } from './OdpsRecipient'

export type CreateFormValues = PenaltyCreateFormValues

type CreateFormProps = {
  isSubmitting?: boolean
  onSuccess: (values: CreateFormValues) => void
}

const defaultValues: Partial<PenaltyCreateFormValues> = {
  paymentType: '10',
}

export function CreateForm({ isSubmitting, onSuccess }: CreateFormProps) {
  const form = useForm<PenaltyCreateFormValues>({
    resolver: zodResolver(penaltyCreateSchema),
    defaultValues,
  })

  const selectedCar = form.watch('car') as CarFormOption | null | undefined
  const selectedDriver = form.watch('driver') as
    | DriverFormOption
    | null
    | undefined
  const selectedCompany = form.watch('company') as
    | CompanyFetchOption
    | null
    | undefined

  /*
   * Выбор из справочника заполняет поля штрафа: VIN и UUID водителя должны
   * совпадать с данными TMS, руками их не вводят.
   * */
  useEffect(() => {
    form.setValue('carExtId', selectedCar?.carExtId ?? '')
    form.setValue('carVin', selectedCar?.carVin ?? '', {
      shouldValidate: form.formState.isSubmitted,
    })
    form.setValue('carPlateNumber', selectedCar?.carPlateNumber ?? '', {
      shouldValidate: form.formState.isSubmitted,
    })
    form.setValue('carSts', selectedCar?.carSts ?? '')
    form.setValue('carBrandTitle', selectedCar?.carBrandTitle ?? '')
    form.setValue('carModelTitle', selectedCar?.carModelTitle ?? '')
    form.setValue('tmsSubdivision', selectedCar?.tmsSubdivision ?? '')
    form.setValue('tmsSubdivisionName', selectedCar?.tmsSubdivisionName ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCar])

  useEffect(() => {
    form.setValue('tmsDriver', selectedDriver?.tmsDriver ?? '', {
      shouldValidate: form.formState.isSubmitted,
    })
    form.setValue('tmsDriverName', selectedDriver?.tmsDriverName ?? '', {
      shouldValidate: form.formState.isSubmitted,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriver])

  useEffect(() => {
    form.setValue('paymentOrgInn', selectedCompany?.id ?? '', {
      shouldValidate: form.formState.isSubmitted,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany])

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <Container>
        <ContainerTitle>Основная информация</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <TextInput
              label='Номер постановления (УИН)'
              name='paymentUin'
              placeholder='20 или 25 цифр'
            />
          </Grid>
          <Grid xs={3}>
            <DatePickerInput label='Дата постановления' name='protocolDate' />
          </Grid>
          <Grid xs={2}>
            <DatePickerInput label='Дата нарушения' name='penaltyDate' />
          </Grid>
          <Grid xs={1}>
            <MaskInput
              label='Время'
              name='penaltyTime'
              mask='00:00'
              placeholder='19:05'
            />
          </Grid>

          <Grid xs={6}>
            <AsyncAutocompleteInput<CompanyFetchOption>
              label='Организация, на которую выставлен штраф'
              name='company'
              fetchOptions={Company.fetchOptionsWithInn}
            />
          </Grid>
          <Grid xs={6}>
            <TextInput label='ИНН организации' name='paymentOrgInn' readOnly />
          </Grid>
        </Grid>
      </Container>

      <Container>
        <ContainerTitle>Автомобиль и водитель</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <AsyncAutocompleteInput<CarFormOption>
              label='Автомобиль'
              name='car'
              placeholder='Гос. номер или VIN'
              fetchOptions={Car.fetchOptions}
            />
          </Grid>
          <Grid xs={6}>
            <AsyncAutocompleteInput<DriverFormOption>
              label='Водитель'
              name='driver'
              placeholder='ФИО водителя'
              fetchOptions={Driver.fetchOptions}
            />
          </Grid>

          <Grid xs={3}>
            <TextInput label='Гос. номер' name='carPlateNumber' readOnly />
          </Grid>
          <Grid xs={3}>
            <TextInput label='VIN' name='carVin' readOnly />
          </Grid>
          <Grid xs={3}>
            <TextInput label='СТС' name='carSts' readOnly />
          </Grid>
          <Grid xs={3}>
            <TextInput
              label='Подразделение'
              name='tmsSubdivisionName'
              readOnly
            />
          </Grid>
        </Grid>
      </Container>

      <Container>
        <ContainerTitle>Нарушение</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={3}>
            <TextInput
              label='Статья'
              name='penaltyLawArticleNumber'
              placeholder='12.9.2'
            />
          </Grid>
          <Grid xs={9}>
            <TextInput
              label='Описание нарушения'
              name='penaltyLawArticleDescription'
              placeholder='Превышение скорости движения ТС от 20 до 40 км/ч'
            />
          </Grid>
          <Grid xs={12}>
            <TextareaInput label='Место нарушения' name='penaltyLocation' />
          </Grid>
        </Grid>
      </Container>

      <Container>
        <ContainerTitle>Начисление</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={3}>
            <NumericInput
              label='Сумма штрафа (без скидки)'
              name='paymentAmountTotal'
              numericProps={{ suffix: ' руб' }}
            />
          </Grid>
          <Grid xs={3}>
            <NumericInput
              label='К оплате'
              name='paymentAmount'
              numericProps={{ suffix: ' руб' }}
            />
          </Grid>
          <Grid xs={3}>
            <NumericInput
              label='Размер скидки'
              name='paymentDiscountSize'
              numericProps={{ suffix: ' %', decimalScale: 0 }}
            />
          </Grid>
          <Grid xs={3}>
            <DatePickerInput
              label='Скидка действует до'
              name='paymentDiscountDate'
            />
          </Grid>
          <Grid xs={3}>
            <DatePickerInput label='Оплатить до' name='paymentUntilDate' />
          </Grid>
        </Grid>
      </Container>

      <OdpsRecipient form={form} />

      <Container>
        <ContainerTitle>Комментарий</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={12}>
            <TextareaInput label='Комментарий' name='comment' />
          </Grid>
        </Grid>
      </Container>

      <FormButtons loading={isSubmitting} isCreating />
    </FormProvider>
  )
}
